//
// IconSelect - Dropdown selection component with icon/image support
// Displays a button that opens a dropdown menu with selectable options
//
// const options = [
//   {
//     divider: true,
//     value: '',
//     ...otherProps,
//   },
// ];
//

import PropTypes from 'prop-types';
import {
  useState,
  useCallback,
  useMemo,
  memo,
} from 'react';

import { emptyObject } from '../../utils';

const IconSelect = memo(
  (props) => {
    const {
      className,
      style = emptyObject,
      popupClassName,
      popupStyle = emptyObject,
      options,
      select,
      selected,
      DisplayMenuItem,
      DisplayClosed,
      defaultText = '',
      clearText,
      disabled,
    } = props;
    const [open, setOpen] = useState(false);
    const [maxHeight, setMaxHeight] = useState();

    const Display = useMemo(
      () => (DisplayClosed || DisplayMenuItem),
      [DisplayClosed, DisplayMenuItem],
    );

    const selectedProps = useMemo(
      () => {
        const allProps = options.find(({ value }) => (value === selected));
        return (allProps);
      },
      [options, selected],
    );

    const handleMainClick = useCallback(
      (event) => {
        const { pageY } = event;
        const { innerHeight } = window;
        const newMaxHeight = (innerHeight - 240 - pageY) * 2;
        // debugger;
        // console.log(`pageY=${pageY} innerHeight=${innerHeight} newMaxHeight=${newMaxHeight}`);
        setMaxHeight(newMaxHeight);
        setOpen(true);
      },
      [],
    );

    const handleMenuClick = useCallback(
      (value) => () => {
        select(value);
        setOpen(false);
      },
      [select],
    );

    let popup;
    if (open) {
      popup = (
        <div
          className={`absolute z-[3000] bg-white rounded-md border shadow-card${(popupClassName) ? ` ${popupClassName}` : ''}`}
          style={{
            ...popupStyle,
          }}
        >
          <div
            className="relative flex flex-col overflow-auto h-auto"
            style={{
              maxHeight,
            }}
          >
            <button
              type="button"
              onClick={handleMenuClick(undefined)}
              className="w-full text-left hover:bg-pxBgSubtle transition-colors"
            >
              {(clearText)
                ? <div className="p-2">{clearText}</div>
                : (
                    <DisplayMenuItem
                      value={null}
                      className="p-2"
                    />
                  )}
            </button>

            {options.map((thisOption) => {
              const { divider, value, ...otherProps } = thisOption;

              if (divider) return <p key={`divider-${value}`} className="border-t border-pxMainOutline my-1">---</p>;

              const isSelected = (value === selected);

              return (
                <button
                  key={value}
                  type="button"
                  value={value}
                  onClick={handleMenuClick(value)}
                  className="w-full text-left hover:bg-pxBgSubtle transition-colors"
                >
                  <DisplayMenuItem
                    value={value}
                    className={`p-2${(isSelected) ? ' font-bold bg-pxInfoBg' : ''}`}
                    {...otherProps}
                  />
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className="p-1.5">
        <button
          type="button"
          onClick={handleMainClick}
          disabled={open || disabled}
          className="disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {(selectedProps)
            ? (
                <Display
                  className={className}
                  style={{ ...style }}
                  {...selectedProps}
                  baseDisplay
                />
              )
            : (
                <div className={className}>
                  {defaultText}
                </div>
              )}
        </button>

        {popup}
      </div>
    );
  },
);

IconSelect.propTypes = {
  className: PropTypes.string,
  style: PropTypes.shape({}),
  popupClassName: PropTypes.string,
  popupStyle: PropTypes.shape({}),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  select: PropTypes.func.isRequired,
  selected: PropTypes.string,
  DisplayMenuItem: PropTypes.func.isRequired,
  DisplayClosed: PropTypes.func,
  defaultText: PropTypes.string,
  clearText: PropTypes.string,
  disabled: PropTypes.bool,
};

IconSelect.displayName = 'IconSelect';

export default IconSelect;
